<template>
  <footer class="main-footer bg-black-50 pt-4 pb-16 border-t-1 border-gray-50 lg:py-10">
    <div class="container mx-auto">
      <etg-collapse
        v-if="isMobileMenuShown"
        class="mobile-links"
        :list="footerLinks"
        container-class="m-0"
        header-class="text-gray-10 pb-0"
        :accordion="true"
        icon-color="gray-10"
      >
        <template #content="{ item }">
          <div v-for="link in item.links" :key="link.name" class="flex flex-col">
            <component
              :is="link.component"
              v-if="link.show"
              v-bind="link.props"
              class="block px-5 mb-6 ml-4 cursor-pointer no-underline text-lg text-gray-10"
            >
              {{ link.text }}
            </component>
          </div>
        </template>
      </etg-collapse>
      <div v-else class="links flex gap-6 mb-32">
        <div class="links-group flex flex-col items-start w-full gap-6">
          <div class="links-group-title">
            <span class="text-xl text-gray-10 cursor-default">{{ $t('footer.download_app.title') }}</span>
          </div>
          <template v-for="item in appIcons" :key="item.name">
            <a :href="item.href" target="_blank" :aria-label="item.name">
              <etg-img class="text-gray-10 w-164px" :src="item.src" :alt="item.name" />
            </a>
          </template>
        </div>
        <div v-for="group in footerLinks" :key="group.name" class="links-group flex flex-col items-start w-full">
          <span class="links-group-title mb-3 text-xl text-gray-10">{{ group.title }}</span>
          <div class="flex flex-col gap-1">
            <template v-for="link in group.links" :key="link.name">
              <component
                :is="link.component"
                v-if="link.show"
                v-bind="link.props"
                class="py-2 w-full cursor-pointer no-underline text-lg text-gray-10 transition-colors hover:text-red-50"
              >
                {{ link.text }}
              </component>
            </template>
          </div>
        </div>
      </div>
      <n-divider class="!my-4 px-5 sm:px-0" />
      <div class="brand px-5 flex flex-col-reverse justify-between gap-3 sm:px-0 md:flex-row">
        <div class="logo flex justify-between items-end gap-2 md:justify-start md:items-center md:gap-4">
          <etg-logo width="107" height="40" />
          <span class="text-xs text-gray-50 md:text-sm">{{ copyrightText }}</span>
        </div>
        <div class="socials flex gap-3 md:flex-row">
          <template v-for="social in SOCIALS" :key="social.src">
            <a v-if="social.show" :href="social.href" target="_blank" :aria-label="social.name">
              <etg-img :src="social.src" :alt="social.name" class="w-6 h-6 text-gray-10 hover:opacity-75 md:w-10 md:h-10" />
            </a>
          </template>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { NDivider } from 'naive-ui'
import { useAppStore } from '@/store/app'

import { facebookLangMap, instagramLangMap, getBlogLangMap, CAREERS_LINK, YOUTUBE_LINK, getFaqLinkUrl, getJoinUsLinkUrl } from '@/constants/links'
import { QueryStringKeys } from '@/constants/commons'

import LocaleLink from '@/components/global/locale-link.vue'

const { t, locale } = useI18n()
const route = useRoute()

const appStore = useAppStore()

const { regionId } = useRegion()
const { appIcons } = useDownloadLinks()

const isIndexPage = computed(() => checkIfIndexPage(route.name))
const hasCsQuery = computed(() => route.query[QueryStringKeys.CS_CHAT] === 'true')
const isCsAutoOpen = computed(() => isIndexPage.value && hasCsQuery.value)

const { isCsLoaded, handleOpen } = useCustomerService()

const footerLinks = computed(() => [
  {
    name: 'explore',
    title: t('footer.explore_eatigo.title'),
    links: [
      { name: 'about-us', text: t('footer.about_us'), props: { to: '/about-us' }, show: true, component: LocaleLink },
      {
        name: 'blog',
        text: t('footer.blog'),
        props: { href: getBlogLangMap(locale.value)[regionId.value], target: '_blank' },
        show: true,
        component: LocaleLink,
      },
      { name: 'careers', text: t('footer.careers'), props: { href: CAREERS_LINK, target: '_blank' }, show: true, component: LocaleLink },
    ],
    custom: true,
  },
  {
    name: 'policies',
    title: t('footer.policies.title'),
    links: [
      {
        name: 'terms-of-use',
        text: t('footer.terms_of_use'),
        props: { to: '/terms-of-use', target: '_blank' },
        show: true,
        component: LocaleLink,
      },
      {
        name: 'privacy-policy',
        text: t('footer.privacy_policy'),
        props: { to: '/privacy-policy', target: '_blank' },
        show: true,
        component: LocaleLink,
      },
      {
        name: 'fair-user-policy',
        text: t('footer.fair_user_policy'),
        props: { to: '/fair-user-policy', target: '_blank' },
        show: true,
        component: LocaleLink,
      },
    ],
    custom: true,
  },
  {
    name: 'support',
    title: t('footer.support.title'),
    links: [
      {
        name: 'contact-us',
        text: t('footer.contact_us'),
        props: { onClick: handleOpen },
        show: isCsLoaded.value,
        component: 'div',
      },
      {
        name: 'faq',
        text: t('footer.faq'),
        props: { href: getFaqLinkUrl(locale.value), target: '_blank' },
        show: true,
        component: LocaleLink,
      },
    ],
    custom: true,
  },
  {
    name: 'partner',
    title: t('footer.partner.title'),
    links: [
      {
        name: 'join-us',
        text: t('footer.join_us'),
        props: { href: getJoinUsLinkUrl(locale.value), target: '_blank' },
        show: true,
        component: LocaleLink,
      },
    ],
    custom: true,
  },
])

const SOCIALS = computed(() => [
  { name: 'instagram', src: '@/assets/icons/footer-instagram.svg', href: instagramLangMap[regionId.value], show: true },
  { name: 'facebook', src: '@/assets/icons/footer-facebook.svg', href: facebookLangMap[regionId.value], show: true },
  { name: 'youtube', src: '@/assets/icons/footer-youtube.svg', href: YOUTUBE_LINK, show: true },
])

const isMobileMenuShown = computed(() => appStore.isMobileScreen || appStore.isDesktop)
const copyrightText = computed(() => t('footer.copyright', { year: new Date().getFullYear() }))

watch(isCsLoaded, openCsChatIfNeeded, { immediate: true })

function openCsChatIfNeeded(_isCsLoaded: boolean) {
  if (!isCsAutoOpen.value) {
    return
  }

  appStore.isFullPageLoadingShown = !_isCsLoaded

  if (_isCsLoaded) {
    handleOpen()
  }
}
</script>

<style lang="scss" scoped>
:deep(.n-collapse) {
  .n-collapse-item {
    .n-collapse-item__header {
      @apply py-3 pl-5 pr-3.5 gap-3;
    }
  }
}
</style>
